import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Handshake, Plus, Save, Trash2, Send, Eye, Edit3, Upload, FileText,
  X, Tag, Power, ChevronRight,
} from "lucide-react";
import { sanitizeHtml } from "../../lib/safeMarkdown";

const PLACEHOLDERS = [
  "first_name", "customer_name", "customer_email",
  "origin", "destination", "pickup_date", "pickup_time",
  "total_price", "quote_id", "partner_name",
];

const emptyDraft = () => ({
  invite_id: null,
  name: "",
  keywords: [],
  subject: "",
  body_html: "",
  attachment_url: "",
  attachment_filename: "",
  from_email: "",
  reply_to: "",
  preheader: "",
  full_html: false,
  enabled: true,
});

// Starter template the operator can drop in when switching to Full-HTML mode
// so they don't start from a blank editor. Includes header, hero, body, CTA,
// footer, and the standard placeholders.
const FULL_HTML_STARTER = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>{partner_name}</title>
</head>
<body style="margin:0;padding:0;background:#050505;font-family:Arial,sans-serif;color:#e5e5e5;">
  <div style="max-width:600px;margin:0 auto;background:#0f0f0f;border:1px solid #333;">
    <!-- HEADER -->
    <div style="background:#1a1a1a;padding:26px;border-bottom:2px solid #C5A059;text-align:center;">
      <h1 style="margin:0;color:#C5A059;font-size:22px;letter-spacing:0.05em;">TAXI NEAR ME</h1>
      <p style="margin:8px 0 0;color:#999;font-size:12px;text-transform:uppercase;letter-spacing:0.15em;">
        {partner_name}
      </p>
    </div>
    <!-- BODY -->
    <div style="padding:28px;font-size:15px;line-height:1.65;">
      <p>Dear {first_name},</p>
      <p>
        Your transfer from <strong>{origin}</strong> to <strong>{destination}</strong>
        on {pickup_date} at {pickup_time} unlocks an exclusive perk with our partner.
      </p>
      <p style="text-align:center;margin:26px 0;">
        <a href="https://example.com/redeem"
           style="background:#C5A059;color:#000;padding:14px 32px;text-decoration:none;
                  font-weight:bold;letter-spacing:0.05em;text-transform:uppercase;">
          Redeem your perk
        </a>
      </p>
      <p>Enjoy &mdash; and safe travels.</p>
      <p><strong>Team TaxiNearMe</strong></p>
    </div>
    <!-- FOOTER -->
    <div style="background:#1a1a1a;padding:14px;text-align:center;border-top:1px solid #333;">
      <p style="margin:0;color:#666;font-size:11px;">
        You received this because you booked a transfer with Taxi Near Me. Booking {quote_id}.
      </p>
    </div>
  </div>
</body>
</html>`;

export default function PartnerInvitesTab() {
  const [invites, setInvites] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/partner-invites`);
      setInvites(res.data.invites || []);
      if (!activeId && res.data.invites?.length) {
        setActiveId(res.data.invites[0].invite_id);
      }
    } catch (e) {
      toast.error("Failed to load partner invites");
    } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => invites.find(i => i.invite_id === activeId) || null,
    [invites, activeId]
  );

  useEffect(() => {
    if (active) {
      setDraft({ ...active });
      setShowPreview(false);
    } else {
      setDraft(emptyDraft());
    }
  }, [active]);

  const dirty = useMemo(() => {
    if (!active) return draft.name || draft.subject || draft.body_html;
    return (
      draft.name !== active.name ||
      draft.subject !== active.subject ||
      draft.body_html !== active.body_html ||
      draft.attachment_url !== active.attachment_url ||
      draft.attachment_filename !== active.attachment_filename ||
      draft.from_email !== active.from_email ||
      draft.reply_to !== (active.reply_to || "") ||
      draft.preheader !== (active.preheader || "") ||
      draft.full_html !== !!active.full_html ||
      draft.enabled !== active.enabled ||
      JSON.stringify(draft.keywords) !== JSON.stringify(active.keywords)
    );
  }, [active, draft]);

  const startNew = () => {
    setActiveId(null);
    setDraft(emptyDraft());
    setShowPreview(false);
  };

  const addKeyword = () => {
    const k = keywordInput.trim().toLowerCase();
    if (!k) return;
    if (draft.keywords.includes(k)) { setKeywordInput(""); return; }
    setDraft(p => ({ ...p, keywords: [...p.keywords, k] }));
    setKeywordInput("");
  };

  const removeKeyword = (k) => {
    setDraft(p => ({ ...p, keywords: p.keywords.filter(x => x !== k) }));
  };

  const insertPlaceholder = (ph) => {
    setDraft(p => ({ ...p, body_html: p.body_html + `{${ph}}` }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/partner-invites/upload-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft(p => ({
        ...p,
        attachment_url: res.data.attachment_url,
        attachment_filename: res.data.attachment_filename,
      }));
      toast.success("Attachment uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    if (!draft.subject.trim() || !draft.body_html.trim()) {
      toast.error("Subject and body are required"); return;
    }
    setSaving(true);
    try {
      if (activeId) {
        const res = await axios.put(`${API}/admin/partner-invites/${activeId}`, draft);
        toast.success("Saved");
        await load();
        setActiveId(res.data.invite_id);
      } else {
        const res = await axios.post(`${API}/admin/partner-invites`, draft);
        toast.success("Partner invite created");
        await load();
        setActiveId(res.data.invite_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!activeId) return;
    if (!window.confirm(`Delete "${active?.name}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/partner-invites/${activeId}`);
      toast.success("Deleted");
      setActiveId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const sendTest = async () => {
    if (!activeId) { toast.error("Save the invite first"); return; }
    if (dirty && !window.confirm("You have unsaved changes — send test with the last saved version?")) return;
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/partner-invites/${activeId}/test`, {});
      toast.success(`Test sent to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTesting(false); }
  };

  const previewHtml = useMemo(() => {
    const sample = {
      first_name: "Mario", customer_name: "Mario Rossi", customer_email: "mario@example.com",
      origin: "Venice Airport", destination: "Noventa Outlet",
      pickup_date: "2026-05-12", pickup_time: "10:30",
      total_price: "180.00", quote_id: "quote_abc123",
      partner_name: draft.name || "Partner",
    };
    let body = draft.body_html;
    Object.entries(sample).forEach(([k, v]) => { body = body.split(`{${k}}`).join(v); });
    return draft.full_html ? body : { __html: sanitizeHtml(body) };
  }, [draft.body_html, draft.name, draft.full_html]);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="partner-invites-tab" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      {/* Sidebar */}
      <aside className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3 space-y-1 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-2 py-2">
          <h3 className="text-white/40 text-[10px] uppercase tracking-wide flex items-center gap-2">
            <Handshake className="w-3 h-3" /> Partner invites
          </h3>
          <button
            onClick={startNew}
            className="p-1 rounded-sm text-[#C5A059] hover:bg-[#C5A059]/10"
            title="New partner invite"
            data-testid="partner-invite-new-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {invites.length === 0 && (
          <p className="text-white/30 text-xs px-3 py-4">
            No partners yet. Click <span className="text-[#C5A059]">+</span> to create your first invite.
          </p>
        )}
        {invites.map(i => (
          <button
            key={i.invite_id}
            onClick={() => setActiveId(i.invite_id)}
            className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-sm text-xs transition-colors ${
              activeId === i.invite_id
                ? "bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
            data-testid={`partner-invite-item-${i.invite_id}`}
          >
            <span className="truncate pr-2 flex items-center gap-2">
              {!i.enabled && <Power className="w-3 h-3 text-amber-400 flex-shrink-0" />}
              {i.name || "(unnamed)"}
            </span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
          </button>
        ))}
      </aside>

      {/* Editor */}
      <section className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg text-white flex items-center gap-2">
              {activeId ? <Edit3 className="w-4 h-4 text-[#C5A059]" /> : <Plus className="w-4 h-4 text-[#C5A059]" />}
              {activeId ? "Edit partner invite" : "New partner invite"}
            </h2>
            <p className="text-white/40 text-xs mt-1">
              Fires after booking confirmed when origin or destination contains any keyword below.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-white/60 mr-2">
              <Switch
                checked={draft.enabled}
                onCheckedChange={(v) => setDraft(p => ({ ...p, enabled: v }))}
                data-testid="partner-invite-enabled-switch"
              />
              {draft.enabled ? "Enabled" : "Disabled"}
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview(v => !v)}
              className="text-white/70 hover:text-white"
              data-testid="partner-invite-preview-btn"
            >
              <Eye className="w-4 h-4 mr-1" /> {showPreview ? "Edit" : "Preview"}
            </Button>
            {activeId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={sendTest}
                disabled={testing}
                className="text-[#C5A059] hover:bg-[#C5A059]/10"
                data-testid="partner-invite-test-btn"
              >
                <Send className="w-4 h-4 mr-1" /> {testing ? "Sending…" : "Send test"}
              </Button>
            )}
            {activeId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={remove}
                className="text-red-400 hover:bg-red-500/10"
                data-testid="partner-invite-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#C5A059] text-black hover:bg-[#B8935A]"
              data-testid="partner-invite-save-btn"
            >
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {showPreview ? (
          <div className="bg-white rounded-sm p-6 min-h-[400px]">
            <p className="text-black/60 text-sm mb-3">
              <strong>Subject:</strong> {draft.subject.replace(/\{first_name\}/g, "Mario")}
            </p>
            <hr className="mb-4" />
            {draft.full_html ? (
              <iframe
                title="Partner invite preview"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full min-h-[800px] border-0"
                data-testid="partner-invite-preview-iframe"
              />
            ) : (
              <div dangerouslySetInnerHTML={previewHtml} data-testid="partner-invite-preview-body" />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="Partner name">
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Cortina Ski Pass"
                  className="input-dark"
                  data-testid="partner-invite-name-input"
                />
              </Field>

              <Field label="Keywords (origin or destination must contain any of these)">
                <div className="input-dark flex flex-wrap items-center gap-2 min-h-[42px] py-2">
                  {draft.keywords.map(k => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 bg-[#C5A059]/15 text-[#C5A059] px-2 py-0.5 rounded-sm text-xs"
                    >
                      <Tag className="w-3 h-3" />{k}
                      <button
                        onClick={() => removeKeyword(k)}
                        className="hover:text-red-400 ml-0.5"
                        data-testid={`partner-invite-remove-keyword-${k}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addKeyword();
                      }
                      if (e.key === "Backspace" && !keywordInput && draft.keywords.length) {
                        removeKeyword(draft.keywords[draft.keywords.length - 1]);
                      }
                    }}
                    placeholder={draft.keywords.length ? "Add another…" : "noventa, cortina, bologna…"}
                    className="flex-1 min-w-[120px] bg-transparent outline-none text-white text-sm"
                    data-testid="partner-invite-keyword-input"
                  />
                </div>
                <p className="text-white/40 text-[10px] mt-1">Press Enter or comma to add. Match is case-insensitive substring.</p>
              </Field>

              <Field label="Subject line (supports placeholders)">
                <Input
                  value={draft.subject}
                  onChange={(e) => setDraft(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Your exclusive invite for {first_name}"
                  className="input-dark"
                  data-testid="partner-invite-subject-input"
                />
              </Field>

              <Field label="From email (leave blank to use default)">
                <Input
                  value={draft.from_email}
                  onChange={(e) => setDraft(p => ({ ...p, from_email: e.target.value }))}
                  placeholder='Taxi Near Me <booking@bookings.taxinearme.it>'
                  className="input-dark"
                  data-testid="partner-invite-from-input"
                />
              </Field>

              <Field label="Reply-to (optional — where replies land)">
                <Input
                  value={draft.reply_to}
                  onChange={(e) => setDraft(p => ({ ...p, reply_to: e.target.value }))}
                  placeholder="e.g. partners@taxinearme.it"
                  className="input-dark"
                  data-testid="partner-invite-replyto-input"
                />
              </Field>

              <Field label="Preheader (inbox preview text — supports placeholders)">
                <Input
                  value={draft.preheader}
                  onChange={(e) => setDraft(p => ({ ...p, preheader: e.target.value }))}
                  placeholder="Show this next to the subject in the inbox"
                  className="input-dark"
                  data-testid="partner-invite-preheader-input"
                />
              </Field>

              <Field label="Attachment (PDF or image, max 10 MB)">
                <div className="space-y-2">
                  {draft.attachment_url && (
                    <div className="flex items-center gap-2 bg-[#0f0f0f] border border-white/5 rounded-sm px-3 py-2 text-xs">
                      <FileText className="w-4 h-4 text-[#C5A059]" />
                      <span className="text-white/70 truncate flex-1">
                        {draft.attachment_filename || draft.attachment_url}
                      </span>
                      <button
                        onClick={() => setDraft(p => ({ ...p, attachment_url: "", attachment_filename: "" }))}
                        className="text-red-400 hover:text-red-300"
                        title="Remove attachment"
                        data-testid="partner-invite-attachment-clear"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="partner-invite-attachment-file"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="text-white/70 hover:text-[#C5A059] border border-white/10"
                      data-testid="partner-invite-attachment-upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload file"}
                    </Button>
                    <span className="text-white/30 text-xs">or paste a URL:</span>
                  </div>
                  <Input
                    value={draft.attachment_url}
                    onChange={(e) => setDraft(p => ({ ...p, attachment_url: e.target.value }))}
                    placeholder="/outlet-invitation.pdf or https://…"
                    className="input-dark"
                    data-testid="partner-invite-attachment-url-input"
                  />
                  <Input
                    value={draft.attachment_filename}
                    onChange={(e) => setDraft(p => ({ ...p, attachment_filename: e.target.value }))}
                    placeholder="Filename shown in the email (e.g. Cortina-Ski-Pass.pdf)"
                    className="input-dark"
                    data-testid="partner-invite-attachment-filename-input"
                  />
                </div>
              </Field>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-white/60 text-xs uppercase tracking-wide">Email HTML body</span>
                <label className="flex items-center gap-2 text-[11px] text-white/60">
                  <Switch
                    checked={draft.full_html}
                    onCheckedChange={(v) => {
                      setDraft(p => {
                        // If flipping ON and body is empty, offer the starter.
                        if (v && !p.body_html.trim()) {
                          return { ...p, full_html: v, body_html: FULL_HTML_STARTER };
                        }
                        return { ...p, full_html: v };
                      });
                    }}
                    data-testid="partner-invite-fullhtml-switch"
                  />
                  Full HTML mode
                </label>
              </div>
              {draft.full_html ? (
                <p className="text-amber-300/80 text-[11px] bg-amber-500/5 border border-amber-500/20 rounded-sm px-3 py-2">
                  <strong>Full HTML mode is ON.</strong> Your HTML below becomes the entire email &mdash;
                  header, footer, styles, everything. The branded wrapper is skipped. Use the button below
                  to load a starter template if you need one.
                </p>
              ) : (
                <p className="text-white/40 text-[11px]">
                  We wrap this body in the standard TaxiNearMe frame (logo, footer, cancellation note).
                  Turn on <strong className="text-white/70">Full HTML mode</strong> to control every pixel.
                </p>
              )}
              <textarea
                value={draft.body_html}
                onChange={(e) => setDraft(p => ({ ...p, body_html: e.target.value }))}
                rows={draft.full_html ? 22 : 14}
                placeholder={draft.full_html ? "<!DOCTYPE html>… paste your full email HTML here" : "<p>Dear {first_name},</p>…"}
                className="input-dark w-full font-mono text-xs leading-relaxed"
                data-testid="partner-invite-body-textarea"
              />
              {draft.full_html && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (draft.body_html.trim() && !window.confirm("Replace current HTML with the starter template?")) return;
                    setDraft(p => ({ ...p, body_html: FULL_HTML_STARTER }));
                  }}
                  className="text-white/60 hover:text-[#C5A059] text-xs"
                  data-testid="partner-invite-load-starter-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" /> Load starter HTML
                </Button>
              )}
              <div className="bg-[#0f0f0f] border border-white/5 rounded-sm p-3">
                <p className="text-white/40 text-[10px] uppercase tracking-wide mb-2">Insert placeholder</p>
                <div className="flex flex-wrap gap-1.5">
                  {PLACEHOLDERS.map(ph => (
                    <button
                      key={ph}
                      onClick={() => insertPlaceholder(ph)}
                      className="text-[10px] px-2 py-1 rounded-sm bg-white/5 text-white/70 hover:bg-[#C5A059]/15 hover:text-[#C5A059] transition-colors"
                      data-testid={`partner-invite-insert-${ph}`}
                    >
                      {`{${ph}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-white/60 text-xs uppercase tracking-wide block mb-1">{label}</span>
      {children}
    </label>
  );
}
